import { z } from 'zod';

import { IataSchema, MiceMoneySchema, NumericIdSchema } from './mice_refs';

// Folha: `mice_invites` e `mice_participants` importam daqui, nunca o contrário (evita o ciclo do
// plano 2); só a folha `mice_refs` entra aqui.

const DirectionSchema = z.enum(['OUTWARD', 'RETURN']);

/** Um voo como o viajante o vê: sem hops nem veredito, só o que a tela mostra. */
export const MiceTravelerFlightSchema = z.object({
  from: IataSchema,
  to: IataSchema,
  /** Horário LOCAL do aeroporto de partida, como o modal-session o entrega: um `Z` no fim é
   *  espúrio (não é UTC). Mostre o relógio como veio; nunca converta fuso (`new Date(...)` no
   *  navegador desloca a hora). */
  departAt: z.iso.datetime({ offset: true, local: true }),
  /** Horário LOCAL do aeroporto de chegada; mesma regra de `departAt` (não converter fuso). */
  arriveAt: z.iso.datetime({ offset: true, local: true }),
  carrier: z.string(),
  flightNumber: z.string(),
  stops: z.number().int().nonnegative(),
  durationMinutes: z.number().int().nullable(),
  /** O preço do voo: a tarifa cotada no recomendado, o da busca nos outros. Null no que o mice
   *  gravou antes do campo. */
  price: MiceMoneySchema.nullable().default(null),
});
export type MiceTravelerFlight = z.infer<typeof MiceTravelerFlightSchema>;

export const MiceTravelerFitSchema = z.object({
  kind: z.enum(['arrival_before_limit', 'departure_after_end', 'arrives_day_before']),
  minutes: z.number().int().nullable(),
  day: z
    .string()
    .regex(/^\d{2}\/\d{2}$/)
    .nullable(),
});
export type MiceTravelerFit = z.infer<typeof MiceTravelerFitSchema>;

/** Avisos de regra do card (spec §3, passo 4), tirados do `yellow` da decisão do sentido. Só
 *  informam: não tornam o sentido "fora das regras" nem levam o pedido ao organizador. */
export const MiceTravelerWarningSchema = z.enum([
  'arrives_during_event',
  'leaves_before_end',
  'tolerance_used',
  'request_not_found',
  'outside_requested_period',
]);
export type MiceTravelerWarning = z.infer<typeof MiceTravelerWarningSchema>;

export const MiceTravelerDirectionSchema = z.object({
  direction: DirectionSchema,
  recommended: MiceTravelerFlightSchema.nullable(),
  fit: MiceTravelerFitSchema.nullable(),
  outsideRules: z.boolean(),
  requested: MiceTravelerFlightSchema.nullable(),
  requestedId: z.string().nullable(),
  requestedOutsideRules: z.boolean(),
  /** Preço do pedido − tarifa do recomendado (positivo: o recomendado sai mais barato); null sem os
   *  dois preços ou com moedas diferentes. */
  difference: MiceMoneySchema.nullable(),
  warnings: z.array(MiceTravelerWarningSchema).default([]),
  /** Opções do sentido sem regra ferida, contando a indicada (o "Ver N voos" da lista). */
  listCount: z.number().int().nonnegative().default(0),
  /** A menor diferença não negativa entre as outras opções (dentro das regras; zero no empate);
   *  null sem nenhuma. */
  nextDifference: MiceMoneySchema.nullable().default(null),
  /** O indicado é o mais barato da lista: nenhuma outra opção dentro das regras sai mais barata
   *  (false sem indicado na lista, com moedas diferentes e na view de um mice antigo). */
  cheapest: z.boolean().default(false),
  /** O viajante deixou uma preferência e a busca a atendeu (sem `outside_requested_period` nem
   *  `request_not_found`). */
  preferenceHeard: z.boolean().default(false),
});
export type MiceTravelerDirection = z.infer<typeof MiceTravelerDirectionSchema>;

/** Item da lista da faixa do evento ("nenhum voo serve"). */
export const MiceTravelerOptionSchema = z.object({
  id: z.string(),
  direction: DirectionSchema,
  flight: MiceTravelerFlightSchema,
  outsideRules: z.boolean(),
  /** Preço da opção − preço do voo indicado pelo agente; null sem indicado ou com moedas
   *  diferentes. */
  difference: MiceMoneySchema.nullable().default(null),
  /** Comprar esta opção dispensa o organizador (dentro do limite do evento e das regras). */
  direct: z.boolean().default(false),
  /** Só `arrives_day_before`: a opção chega na véspera do evento. */
  fit: MiceTravelerFitSchema.nullable().default(null),
});
export type MiceTravelerOption = z.infer<typeof MiceTravelerOptionSchema>;

/** O pedido do viajante que espera a organização, como ele o mandou: o voo que seria comprado em
 *  cada sentido da viagem (o escolhido onde mudou, o recomendado onde não), a justificativa e quando. */
export const MiceTravelerRequestViewSchema = z.object({
  requestedAt: z.iso.datetime(),
  justification: z.string(),
  directions: z
    .array(
      z.object({
        direction: DirectionSchema,
        /** Null num sentido sem escolha e sem recomendado (nada a comprar). */
        flight: MiceTravelerFlightSchema.nullable(),
        outsideRules: z.boolean(),
      }),
    )
    .min(1)
    .max(2),
});
export type MiceTravelerRequestView = z.infer<typeof MiceTravelerRequestViewSchema>;

export const MiceTravelerFlightsSchema = z.object({
  version: z.number().int().positive(),
  directions: z.array(MiceTravelerDirectionSchema).min(1).max(2),
  options: z.array(MiceTravelerOptionSchema).default([]),
  refusedAt: z.iso.datetime().nullable().default(null),
  /** O pedido pendente nesta versão; null sem pedido (e na view de um mice-api antigo). */
  request: MiceTravelerRequestViewSchema.nullable().default(null),
});
export type MiceTravelerFlights = z.infer<typeof MiceTravelerFlightsSchema>;

const JustificationSchema = z.string().trim().min(1).max(500);
const VersionSchema = z.number().int().positive();

export const MiceTravelerConfirmRequestSchema = z.object({
  travelerId: NumericIdSchema,
  version: VersionSchema,
  justification: JustificationSchema.optional(),
});
export type MiceTravelerConfirmRequest = z.infer<typeof MiceTravelerConfirmRequestSchema>;

export const MiceTravelerOtherFlightRequestSchema = z.object({
  travelerId: NumericIdSchema,
  version: VersionSchema,
  text: JustificationSchema,
});
export type MiceTravelerOtherFlightRequest = z.infer<typeof MiceTravelerOtherFlightRequestSchema>;

export const MiceTravelerChooseRequestSchema = z
  .object({
    travelerId: NumericIdSchema,
    version: VersionSchema,
    choices: z
      .array(
        z.object({
          direction: DirectionSchema,
          candidateId: z.string().min(1),
          source: z.enum(['suggestion', 'event_list']).default('suggestion'),
        }),
      )
      .min(1)
      .max(2),
    /** Sem ela, só a escolha da lista que cabe no limite do evento (a mice-api decide). */
    justification: JustificationSchema.optional(),
  })
  .refine((b) => new Set(b.choices.map((c) => c.direction)).size === b.choices.length, {
    message: 'Um voo por sentido',
    params: { code: 'duplicate_direction' },
  });
export type MiceTravelerChooseRequest = z.infer<typeof MiceTravelerChooseRequestSchema>;
